import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Checkbox, Flex, Text, TextButton, Tooltip } from '@modusign/design-system';

import LoadMoreButton from '@app/components/LoadMoreButton';
import SelectedMemberGroupArea from '@app/features/authorization/components/PermissionAssigneeListModal/AddAssigneeForm/SelectedMemberGroupArea';
import ResourceRoleSubjectLabel from '@app/features/authorization/components/ResourceRoleSubjectLabel';
import {
  adaptWorkspaceGroup,
  ResourceRoleSubject,
  WorkspaceGroup,
} from '@app/features/workspaceManagement/types';
import {
  useAllRolesAndSubjects,
  useInfiniteListGroups,
} from '@app/features/workspaceManagement/workspaceMemberInvitation/hooks';
import useDebouncedState from '@app/hooks/useDebouncedState';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

const PAGE_LIMIT = 10;

interface WorkspaceGroupSelectListProps {
  disabled?: boolean;
  selectedGroups: WorkspaceGroup['subjectId'][];
  onToggleGroup: (ids: WorkspaceGroup['subjectId'][]) => void;
  tooltipContent?: React.ReactNode;
}

export default function WorkspaceGroupSelectList({
  disabled,
  selectedGroups,
  onToggleGroup,
  tooltipContent,
}: WorkspaceGroupSelectListProps) {
  const workspaceId = useWorkspaceId();
  const [debouncedKeyword, searchKeyword, setSearchKeyword] = useDebouncedState('', 300);
  const {
    data: groups,
    hasNextPage,
    isFetchingNextPage,
    status,
    fetchNextPage,
  } = useInfiniteListGroups(
    workspaceId,
    { limit: PAGE_LIMIT, name: debouncedKeyword || undefined },
    {
      select: ({ pageParams, pages }) => ({
        pageParams,
        pages: pages.map(({ count, groups }) => ({
          count: count - 1,
          groups: groups.filter((g) => g.type !== 'ALL_MEMBERS').map(adaptWorkspaceGroup),
        })),
      }),
    }
  );
  const { resourceRoleSubjects } = useAllRolesAndSubjects(workspaceId);

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between" mb={7}>
        <Text fontSize="md" fontWeight="medium">
          선택한 그룹
        </Text>
      </Flex>
      <SelectedMemberGroupArea
        keyword={searchKeyword}
        selectedResourceRoleSubjects={
          resourceRoleSubjects.data?.filter((rrs) => selectedGroups.includes(rrs.subjectId)) ?? []
        }
        onKeywordChange={setSearchKeyword}
        onSelectedResourceRoleSubjectsChange={(vs) =>
          onToggleGroup(
            selectedGroups.filter((groupId) => !vs.some((v) => v.subjectId === groupId))
          )
        }
      />
      <Box
        borderBottom="1px solid"
        borderBottomColor="tableBorderNeutralLight"
        fontSize="md"
        fontWeight="medium"
        pb={4}
        pt={7}
      >
        {debouncedKeyword ? (
          <Flex justifyContent="space-between">
            <Text fontWeight="medium">{`'${debouncedKeyword}' 검색 결과`}</Text>
            <TextButton onClick={() => setSearchKeyword('')}>
              <Box as={FontAwesomeIcon} icon={faArrowRotateLeft} />
              <Text ml="2px">검색 초기화</Text>
            </TextButton>
          </Flex>
        ) : (
          <Text fontSize="md" fontWeight="medium">
            전체 그룹
          </Text>
        )}
      </Box>

      <SearchedGroupList>
        {groups?.pages
          ?.flatMap((page) => page.groups)
          .map((g) => (
            <ListItem
              disabled={disabled && !selectedGroups.includes(g.subjectId)}
              isSelected={selectedGroups.includes(g.subjectId)}
              key={g.subjectId}
              role={g}
              tooltipContent={tooltipContent}
              onSelect={() => onToggleGroup([g.subjectId])}
            />
          ))}
        <LoadMoreButton
          currentDataCount={groups?.pages?.reduce((acc, page) => acc + page.groups.length, 0) ?? 0}
          dataName="그룹"
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
          maxDataCount={groups?.pages?.[0].count ?? 0}
          status={status}
          onClick={() => fetchNextPage()}
        />
      </SearchedGroupList>
    </>
  );
}

function SearchedGroupList({ children }: { children: React.ReactNode }) {
  return (
    <Flex
      as="ul"
      borderBottom="1px solid"
      borderBottomColor="tableBorderNeutralBase"
      flexDirection="column"
      height="350px"
      overflowY="auto"
    >
      {children}
    </Flex>
  );
}

interface ListItemProps {
  disabled?: boolean;
  role: ResourceRoleSubject;
  isSelected: boolean;
  tooltipContent?: React.ReactNode;
  onSelect: (checked: boolean) => void;
}

function ListItem({ disabled, isSelected, role, tooltipContent, onSelect }: ListItemProps) {
  return (
    <Flex
      as="li"
      borderBottom="1px solid"
      borderBottomColor="tableBorderNeutralLight"
      sx={{
        '&:hover': {
          bg: 'tableBackgroundNeutralSlightlyDark',
        },
      }}
      width="100%"
    >
      <Flex
        alignItems="center"
        as="label"
        sx={{ gap: 4, p: 4, width: '100%', '&:hover': { cursor: 'pointer' } }}
      >
        <Tooltip content={tooltipContent} disabled={!disabled} placement="bottom" scale="sm">
          <Checkbox
            checked={isSelected}
            disabled={disabled}
            width="14px"
            onChange={(event) => onSelect(event.target.checked)}
          />
        </Tooltip>
        <ResourceRoleSubjectLabel role={role} />
      </Flex>
    </Flex>
  );
}
